import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Lightning, Pencil, Check, Warning, FlagBanner } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide, EmptyState } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  auto_generated: { label: 'Auto Generated', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  flagged: { label: 'Flagged', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  manually_corrected: { label: 'Corrected', color: 'bg-green-50 text-green-700 border-green-200' },
  completed: { label: 'Completed', color: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
};

export const PurchaseSchedule = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/purchase-schedule`, { withCredentials: true });
      setItems(res.data);
    } catch { toast.error('Failed to load schedule'); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/purchase-schedule/generate`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      planned_purchase_date: item.planned_purchase_date || '',
      actual_purchase_date: item.actual_purchase_date || '',
      supplier: item.supplier || '',
      quantity: item.quantity,
      estimated_cost: item.estimated_cost,
      notes: item.notes || '',
      status: item.status === 'flagged' || item.status === 'auto_generated' ? 'manually_corrected' : item.status,
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-schedule/${id}`, editForm, { withCredentials: true });
      toast.success('Updated and saved');
      setEditingId(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleMarkComplete = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-schedule/${id}`, { status: 'completed' }, { withCredentials: true });
      toast.success('Marked as completed');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const flaggedCount = items.filter((i) => i.status === 'flagged').length;
  const totalEstimated = items.reduce((s, i) => s + i.estimated_cost, 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-600">Loading...</div></div>;

  return (
    <div>
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate(`/projects/${projectId}?tab=control`)} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
            <ArrowLeft size={18} className="mr-1.5" /> Back to Control Tower
          </Button>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Purchase Schedule</h1>
            <Button onClick={handleGenerate} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="generate-schedule">
              <Lightning size={18} className="mr-1.5" /> Generate from BOQ
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ModuleGuide text="Auto-generated procurement timeline from the confirmed BOQ. Review flagged items and assign suppliers before raising Purchase Orders." />

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Total Items</span><p className="text-2xl font-bold text-zinc-900 mt-1">{items.length}</p></div>
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Flagged for Review</span><p className="text-2xl font-bold text-amber-700 mt-1">{flaggedCount}</p></div>
          <div className="bg-white border border-zinc-200 p-4"><span className="text-xs font-bold uppercase text-zinc-500">Estimated Total</span><p className="text-2xl font-bold text-blue-700 mt-1">₹{totalEstimated.toLocaleString('en-IN')}</p></div>
        </div>

        {flaggedCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 p-3 mb-4 flex items-center gap-2 text-sm text-amber-800" data-testid="flagged-warning">
            <Warning size={18} /> <strong>{flaggedCount} items</strong> need manual review before purchasing
          </div>
        )}

        {items.length === 0 ? (
          <EmptyState
            title="No schedule items"
            description="Click 'Generate from BOQ' to auto-create a procurement schedule from confirmed quotes. You can then assign suppliers and planned dates."
            testId="schedule-empty-state"
          />
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  {['Material', 'Spec / Make', 'Qty', 'Est. Cost', 'Supplier', 'Plan Date', 'Status', ''].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const sc = statusConfig[item.status] || statusConfig.auto_generated;
                  const isEditing = editingId === item.id;
                  return (
                    <tr key={item.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ps-row-${item.id}`}>
                      <td className="px-3 py-3">
                        <p className="font-medium text-zinc-800">{item.material_description}</p>
                        {item.flag_reason && <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1"><FlagBanner size={10} /> {item.flag_reason}</p>}
                      </td>
                      <td className="px-3 py-3 text-xs text-zinc-600">{item.specification || '—'}<br />{item.make || '—'}</td>
                      <td className="px-3 py-3">
                        {isEditing ? <Input type="number" value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: parseFloat(e.target.value) || 0 })} className="w-20 h-7 text-xs rounded-none" /> : `${item.quantity} ${item.unit}`}
                      </td>
                      <td className="px-3 py-3 font-medium">
                        {isEditing ? <Input type="number" value={editForm.estimated_cost} onChange={(e) => setEditForm({ ...editForm, estimated_cost: parseFloat(e.target.value) || 0 })} className="w-24 h-7 text-xs rounded-none" /> : `₹${item.estimated_cost.toLocaleString('en-IN')}`}
                      </td>
                      <td className="px-3 py-3">
                        {isEditing ? <Input value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} className="w-28 h-7 text-xs rounded-none" /> : (item.supplier || '—')}
                      </td>
                      <td className="px-3 py-3">
                        {isEditing ? <Input type="date" value={editForm.planned_purchase_date} onChange={(e) => setEditForm({ ...editForm, planned_purchase_date: e.target.value })} className="w-32 h-7 text-xs rounded-none" /> : (item.planned_purchase_date || '—')}
                      </td>
                      <td className="px-3 py-3"><span className={`inline-flex text-xs font-medium px-2 py-0.5 border ${sc.color}`}>{sc.label}</span></td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1">
                          {isEditing ? (
                            <button onClick={() => handleSaveEdit(item.id)} className="p-1 text-green-700 hover:bg-green-50" data-testid={`ps-save-${item.id}`}><Check size={14} weight="bold" /></button>
                          ) : (
                            <>
                              <button onClick={() => startEdit(item)} className="p-1 text-zinc-400 hover:text-blue-700" data-testid={`ps-edit-${item.id}`}><Pencil size={14} /></button>
                              {item.status !== 'completed' && (
                                <button onClick={() => handleMarkComplete(item.id)} className="p-1 text-zinc-400 hover:text-green-700" data-testid={`ps-complete-${item.id}`}><Check size={14} /></button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};
